<template>
  <div class="multi-net-switch-page">
    <!-- 空状态 -->
    <div v-if="cardList.length === 0" class="empty-state">
      <i class="el-icon-position empty-icon"></i>
      <p class="empty-title">组网批量换卡 · 暂无待处理组网号</p>
      <p class="empty-desc">
        在下方添加需要换卡的组网号,系统会同时列出当前移动/电信卡号,您在表格里填入新卡号即可。
      </p>
      <el-button type="primary" icon="el-icon-circle-plus-outline" @click="openAddDialog">
        添加组网号
      </el-button>
    </div>

    <!-- 顶部说明条 -->
    <div v-else class="page-body">
      <div class="page-header">
        <div class="header-text">
          <div class="page-title">
            <i class="el-icon-sort title-icon"></i>
            组网批量换卡
          </div>
          <div class="page-subtitle">
            支持同时处理多个组网号,组网号保持不变,只替换其中的移动/电信卡。
            <span class="text-muted">目标卡不区分运营商,但不能与原移动/电信卡重复。</span>
          </div>
        </div>
        <div class="header-actions">
          <el-button type="primary" icon="el-icon-circle-plus-outline" @click="openAddDialog">
            添加组网号
          </el-button>
        </div>
      </div>

      <!-- 表格 -->
      <el-table
        :data="cardList"
        border
        stripe
        v-loading="pageLoading"
        class="data-table"
        :row-class-name="rowClassName"
        size="small"
      >
        <el-table-column type="index" label="序号" align="center" width="60"></el-table-column>
        <el-table-column prop="groupNo" label="组网号" align="center" width="150">
          <template slot-scope="scope">
            <el-link
              v-if="scope.row.groupNo"
              type="primary"
              :underline="false"
              @click="goGroupDetail(scope.row.groupNo)"
            >
              {{ scope.row.groupNo }}
            </el-link>
            <span v-else class="placeholder">--</span>
          </template>
        </el-table-column>
        <el-table-column prop="ydIccid" label="原移动卡" align="center" min-width="180" :show-overflow-tooltip="true">
          <template slot-scope="scope">
            <el-link
              v-if="scope.row.ydIccid"
              type="primary"
              :underline="false"
              @click="goCardDetail(scope.row.ydIccid)"
            >
              {{ scope.row.ydIccid }}
            </el-link>
            <span v-else class="placeholder">无</span>
          </template>
        </el-table-column>
        <el-table-column prop="dxIccid" label="原电信卡" align="center" min-width="180" :show-overflow-tooltip="true">
          <template slot-scope="scope">
            <el-link
              v-if="scope.row.dxIccid"
              type="primary"
              :underline="false"
              @click="goCardDetail(scope.row.dxIccid)"
            >
              {{ scope.row.dxIccid }}
            </el-link>
            <span v-else class="placeholder">无</span>
          </template>
        </el-table-column>
        <el-table-column prop="targetYdIccid" label="目标卡1" align="center" min-width="200">
          <template slot-scope="scope">
            <el-input
              v-model="scope.row.targetYdIccid"
              placeholder="填入新卡号,留空=不换卡"
              size="small"
              clearable
              maxlength="30"
              @input="validateItem(scope.row)"
            ></el-input>
          </template>
        </el-table-column>
        <el-table-column prop="targetDxIccid" label="目标卡2" align="center" min-width="200">
          <template slot-scope="scope">
            <el-input
              v-model="scope.row.targetDxIccid"
              placeholder="填入新卡号,留空=不换卡"
              size="small"
              clearable
              maxlength="30"
              @input="validateItem(scope.row)"
            ></el-input>
          </template>
        </el-table-column>
        <el-table-column prop="remark" label="备注" align="center" min-width="180">
          <template slot-scope="scope">
            <el-input
              v-model="scope.row.remark"
              placeholder="选填"
              size="small"
              clearable
              maxlength="100"
            ></el-input>
          </template>
        </el-table-column>
        <el-table-column label="操作" align="center" width="90" fixed="right">
          <template slot-scope="scope">
            <el-button
              type="text"
              class="danger-text-btn"
              icon="el-icon-delete"
              @click="removeItem(scope.row, scope.$index)"
            >移除</el-button>
          </template>
        </el-table-column>
      </el-table>

      <div v-if="cardList.length > 0" class="add-card-bar">
        <el-button type="primary" plain icon="el-icon-circle-plus-outline" style="width: 100%" @click="openAddDialog">
          添加组网号
        </el-button>
      </div>
    </div>

    <!-- 底部操作栏 -->
    <div class="page-footer" v-if="cardList.length > 0">
      <div class="footer-summary">
        <span>待处理组网号:<strong>{{ cardList.length }}</strong> 个</span>
        <span :class="{ 'text-warn': invalidCount > 0 }">
          待修正条目:<strong>{{ invalidCount }}</strong>
        </span>
        <span class="text-muted">
          已填目标:<strong>{{ filledCount }}</strong>
        </span>
      </div>
      <div class="footer-buttons">
        <el-button icon="el-icon-refresh-left" @click="handleClear">清空全部</el-button>
        <el-button
          type="primary"
          size="medium"
          :loading="submitLoading"
          :disabled="!canSubmit"
          @click="submitSwitch"
        >提交批量换卡</el-button>
      </div>
    </div>

    <!-- 添加组网号弹窗 -->
    <el-dialog
      title="添加组网号"
      :visible.sync="addDialogVisible"
      width="520px"
      append-to-body
      :close-on-click-modal="false"
      @close="resetAddDialog"
    >
      <el-form :model="addForm" ref="addFormRef" :rules="addFormRules" label-width="0">
        <el-form-item prop="groupNos">
          <el-input
            v-model="addForm.groupNos"
            type="textarea"
            :rows="8"
            placeholder="支持批量粘贴,每行/逗号分隔一个组网号"
          ></el-input>
        </el-form-item>
        <div class="add-stat">
          已识别 <strong>{{ addRowCount }}</strong> 个组网号,
          排除重复后新增 <strong>{{ newAddCount }}</strong> 个
        </div>
      </el-form>
      <div slot="footer" class="dialog-footer">
        <el-button @click="addDialogVisible = false">取 消</el-button>
        <el-button type="primary" :loading="addLoading" @click="confirmAddGroupNos">确 定</el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import { selectMultiNetByGroupNos } from '@/api/multiNet'
import { batchSwitchMultiNetCard } from '@/api/multiNet'

export default {
  name: 'multiNetSwitch',
  data() {
    return {
      pageLoading: false,
      submitLoading: false,
      addLoading: false,
      cardList: [],

      // 校验状态外置:{ groupNo: errorMsg }
      // 用普通对象 + $set 触发响应式,避免 el-table row-class-name 不响应 row 内部属性变化的问题
      errorMap: {},

      // 添加组网号弹窗
      addDialogVisible: false,
      addForm: {
        groupNos: ''
      },
      addFormRules: {
        groupNos: [
          { required: true, message: '请输入组网号', trigger: 'blur' }
        ]
      }
    }
  },
  computed: {
    // 解析后的输入(行/逗号/空格/分号)
    parsedGroupNos() {
      if (!this.addForm.groupNos) return []
      return this.addForm.groupNos
        .split(/[\n,;，；\s]+/)
        .map(s => s.trim())
        .filter(s => !!s)
    },
    // 已识别总数
    addRowCount() {
      return this.parsedGroupNos.length
    },
    // 排除已存在的数量
    newAddCount() {
      const existed = this.cardList.map(c => c.groupNo)
      return this.parsedGroupNos.filter(g => !existed.includes(g)).length
    },
    // 无效条数(从 errorMap 取,响应式)
    invalidCount() {
      return Object.keys(this.errorMap).length
    },
    // 已填目标总条数
    filledCount() {
      return this.cardList.filter(c => !!c.targetYdIccid || !!c.targetDxIccid).length
    },
    // 提交按钮是否可点:列表非空 + 无无效 + 至少一条填了目标
    canSubmit() {
      return this.cardList.length > 0
        && this.invalidCount === 0
        && this.filledCount > 0
    }
  },
  created() {
    this.initCards()
  },
  methods: {
    initCards() {
      const fromParams = (this.$route.params && this.$route.params.iccids) || ''
      if (!fromParams) return
      const list = fromParams.split(/[\n,;，；\s]+/).map(s => s.trim()).filter(s => !!s)
      if (list.length === 0) return
      this.loadGroupNos(list)
    },

    /**
     * 添加组网号(去重 → 拉详情 → 装配行)
     */
    loadGroupNos(groupNos) {
      const existed = this.cardList.map(c => c.groupNo)
      const newOnes = groupNos.filter(g => !existed.includes(g))
      if (newOnes.length === 0) {
        const dup = groupNos.join(', ')
        this.$message.warning(`这 ${groupNos.length} 个组网号已在列表中,无需重复添加(${dup})`)
        return
      }
      const skipped = groupNos.filter(g => existed.includes(g))
      this.pageLoading = true
      selectMultiNetByGroupNos(newOnes).then(res => {
        if (res.data.code === 200) {
          const rows = (res.data.data || []).filter(r => r && r.groupNo)
          if (rows.length === 0) {
            this.$message.warning(`以下组网号加载失败或不存在: ${newOnes.join(', ')}`)
            return
          }
          this.cardList = [...this.cardList, ...rows]
          this.cardList.forEach(item => this.validateItem(item))
          if (newOnes.length > rows.length) {
            this.$message.warning(`部分组网号加载失败: ${newOnes.slice(rows.length).join(', ')}`)
          }
          if (skipped.length > 0) {
            this.$message.info(`已跳过 ${skipped.length} 个已存在的组网号`)
          }
        } else {
          this.$message.warning(`以下组网号加载失败或不存在: ${newOnes.join(', ')}`)
        }
      })
      .finally(() => {
        this.pageLoading = false
      })
      
    },

    validateItem(item) {
      if (!item || !item.groupNo) return true
      const { ydIccid, dxIccid, targetYdIccid, targetDxIccid } = item
      let msg = ''
      if (!targetYdIccid && !targetDxIccid) {
        msg = '至少填写一项目标卡号'
      } else if (targetYdIccid && (targetYdIccid === ydIccid || targetYdIccid === dxIccid)) {
        msg = '目标卡号不能与原移动/电信卡重复'
      } else if (targetDxIccid && (targetDxIccid === ydIccid || targetDxIccid === dxIccid)) {
        msg = '目标卡号不能与原移动/电信卡重复'
      } else if (targetYdIccid && targetDxIccid && targetYdIccid === targetDxIccid) {
        msg = '两张目标卡号不能相同'
      }
      if (msg) {
        this.$set(this.errorMap, item.groupNo, msg)
      } else {
        this.$delete(this.errorMap, item.groupNo)
      }
      return !msg
    },

    /**
     * 打开添加组网号
     */
    openAddDialog() {
      this.addDialogVisible = true
      this.$nextTick(() => {
        this.$refs.addFormRef && this.$refs.addFormRef.clearValidate()
      })
    },

    /**
     * 重置添加弹窗
     */
    resetAddDialog() {
      this.addForm.groupNos = ''
      this.$refs.addFormRef && this.$refs.addFormRef.resetFields()
    },

    /**
     * 确认添加组网号
     */
    confirmAddGroupNos() {
      this.$refs.addFormRef.validate(valid => {
        if (!valid) return
        const list = this.parsedGroupNos
        if (list.length === 0) {
          this.$message.warning('请输入有效的组网号')
          return
        }
        this.addLoading = true
        this.loadGroupNos(list)
        setTimeout(() => {
          this.addLoading = false
          this.addDialogVisible = false
        }, 600)
      })
    },

    /**
     * 移除单行
     */
    removeItem(item, index) {
      this.$confirm(`确定移除组网号 ${item.groupNo} 的换卡记录?`, '移除确认', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      })
        .then(() => {
          this.cardList.splice(index, 1)
          this.$message.success('移除成功')
        })
        .catch(() => {})
    },

    /**
     * 清空全部
     */
    handleClear() {
      if (this.cardList.length === 0) return
      this.$confirm('确认清空所有换卡条目?', '清空确认', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      })
      .then(() => {
        this.cardList = []
      })
      .catch(() => {})
    },

    /**
     * 提交批量换卡
     */
    submitSwitch() {
      // 重新校验全部
      let hasError = false
      this.cardList.forEach(item => {
        if (!this.validateItem(item)) hasError = true
      })
      if (hasError) {
        this.$message.warning('请先修正标红条目')
        return
      }
      const ready = this.cardList.filter(c => !!c.targetYdIccid || !!c.targetDxIccid)
      if (ready.length === 0) {
        this.$message.warning('请至少填写一项目标卡号')
        return
      }
      const desc = [
        `待处理组网号: <strong>${ready.length}</strong> 个`].join('<br/>')
      this.$confirm(desc, '确认提交批量换卡?', {
        dangerouslyUseHTMLString: true,
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      })
      .then(() => {
        this.submitLoading = true
        const params = ready.map(item => ({
          groupNo: item.groupNo,
          targetYdIccid: item.targetYdIccid || '',
          targetDxIccid: item.targetDxIccid || '',
          remark: item.remark || ''
        }))
        batchSwitchMultiNetCard(params)
          .then(res => {
            if (res.data && res.data.code === 200) {
              this.$message.success(`批量换卡已提交 ${ready.length} 个,请到任务中心查看进度`)
              // 关闭当前标签页后跳转
              this.$store.dispatch('tagsView/delView', this.$route).then(() => {
                this.$router.push({ path: '/device/card/multiNet' })
              })
            } else {
              this.$message.error((res.data && res.data.message) || '提交失败')
            }
          })
          .finally(() => {
            this.submitLoading = false
          })
      })
      .catch(() => {})
    },

    /**
     * 表格 rowClassName
     */
    rowClassName({ row }) {
      return this.errorMap[row.groupNo] ? 'error-row' : ''
    },

    /**
     * 跳转组网详情
     */
    goGroupDetail(groupNo) {
      if (!groupNo) return
      this.$router.push({ path: '/card/multiNet/info', query: { groupNo } })
    },

    /**
     * 跳转卡详情
     */
    goCardDetail(iccid) {
      if (!iccid) return
      this.$router.push({ path: '/card/detail', query: { iccid } })
    }
  }
}
</script>

<style lang="scss" scoped>
.multi-net-switch-page {
  padding: 16px;
  background: #f0f2f5;
  min-height: 100%;
  padding-bottom: 80px;

  // 弹窗底部按钮居中
  ::v-deep .el-dialog__footer {
    text-align: center;

    .dialog-footer {
      display: inline-block;
    }
  }

  .page-body {
    background: #fff;
    border-radius: 4px;
    padding: 20px;
    min-height: calc(100vh - 220px);

    .page-header {
      display: flex;
      justify-content: space-between;
      align-items: flex-start;
      padding-bottom: 16px;
      margin-bottom: 16px;
      border-bottom: 1px solid #ebeef5;

      .header-text {
        .page-title {
          font-size: 16px;
          font-weight: 600;
          color: #303133;
          display: flex;
          align-items: center;

          .title-icon {
            color: #409eff;
            margin-right: 8px;
            font-size: 18px;
          }
        }
        .page-subtitle {
          margin-top: 6px;
          font-size: 13px;
          color: #606266;
          line-height: 1.6;
          .text-muted {
            color: #909399;
          }
        }
      }
      .header-actions {
        display: flex;
        gap: 8px;
        flex-shrink: 0;
      }
    }

    .data-table {
      ::v-deep tr.error-row td {
        background-color: #fef0f0 !important;
      }
    }

    .add-card-bar {
      margin-top: 16px;
    }
  }

  .empty-state {
    background: #fff;
    border-radius: 4px;
    padding: 80px 20px;
    text-align: center;
    color: #909399;

    .empty-icon {
      font-size: 64px;
      color: #c0c4cc;
      margin-bottom: 16px;
    }
    .empty-title {
      font-size: 16px;
      color: #303133;
      margin-bottom: 8px;
    }
    .empty-desc {
      font-size: 13px;
      line-height: 1.7;
      margin-bottom: 24px;
      max-width: 480px;
      margin-left: auto;
      margin-right: auto;
    }
  }

  .page-footer {
    position: fixed;
    right: 0;
    bottom: 0;
    width: calc(100% - 200px);
    height: 60px;
    background: #fff;
    border-top: 1px solid #ebeef5;
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 0 24px;
    box-shadow: 0 -2px 8px rgba(0, 0, 0, 0.04);
    z-index: 100;

    .footer-summary {
      display: flex;
      gap: 24px;
      color: #606266;
      font-size: 14px;
      strong {
        color: #303133;
        font-size: 16px;
        margin: 0 4px;
      }
      .text-warn strong {
        color: #f56c6c;
      }
      .text-muted strong {
        color: #909399;
      }
    }

    // 按钮组相对整个底部栏绝对定位水平居中
    .footer-buttons {
      position: absolute;
      left: 50%;
      top: 50%;
      transform: translate(-50%, -50%);
    }
  }
}

.add-stat {
  text-align: right;
  color: #606266;
  font-size: 13px;
  margin-top: 8px;
  strong {
    color: #409eff;
    margin: 0 2px;
  }
}

.placeholder {
  color: #c0c4cc;
}

.danger-text-btn {
  color: #f56c6c;
  &:hover {
    color: #f78989;
  }
}

@media (max-width: 768px) {
  .multi-net-switch-page {
    .page-body .page-header {
      flex-direction: column;
      align-items: flex-start;
      gap: 12px;
      .header-actions {
        width: 100%;
      }
    }
    .page-footer {
      width: 100%;
      flex-direction: column;
      height: auto;
      padding: 12px;
      gap: 10px;
    }
  }
}
</style>
